/**
 * 首次启动引导
 * ============================================================
 * 对应需求文档 V1.0 的 A 组（A02 / A04 / A05 / A07 / A08 / A09 / A10 / A11）。
 * 做成**分步向导**而不是 11 个独立路由：移动端标准做法，
 * 状态集中在一处，回退/跳过都好处理。
 *
 * 跳过的两页在 lib/onboarding.js 顶部写了原因（A01 启动页、A03 登录）。
 *
 * 设计原则（沿用需求文档第 5 节）：
 *   · 每一步都能跳过 —— 引导不该拦住用户，尤其"水平评估"这类
 *   · 如实展示方言能力 —— 不把「实验中」的方言包装成成熟可用
 *   · 权限说明必须解释**为什么**要，而不是只弹系统授权框
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { fetchDialects, setSelectedDialectId, getSelectedDialectId, STATUS_STYLE } from '../lib/dialects';
import { buildQuestions, analyzeResult, selectLessonSample, TEST_SIZE } from '../lib/levelTest';
import { setSelectedTutorId } from '../lib/tutorUtils';
import {
  GOALS,
  LEVELS,
  DAILY_PLANS,
  getOnboarding,
  saveOnboarding,
  completeOnboarding,
} from '../lib/onboarding';

const STEPS = ['welcome', 'language', 'dialect', 'goals', 'level', 'tutor', 'permission', 'plan'];
const STEP_TITLES = {
  welcome: '欢迎',
  language: '你的母语',
  dialect: '想学哪种方言',
  goals: '为什么学',
  level: '现在的水平',
  tutor: '选个老师',
  permission: '开始前',
  plan: '每天学多久',
};

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [state, setState] = useState(() => getOnboarding());

  const [dialects, setDialects] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);

  const step = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;

  useEffect(() => {
    (async () => {
      try {
        const [dz, tz] = await Promise.all([
          fetchDialects().catch(() => ({ dialects: [] })),
          api.get('/api/tutors').catch(() => ({ data: {} })),
        ]);
        setDialects(dz.dialects || []);
        const list = tz.data?.data?.tutors || tz.data?.tutors || [];
        setTutors(list);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const patch = useCallback((p) => {
    setState(saveOnboarding(p));
  }, []);

  const next = () => {
    if (isLast) return finish();
    setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  };
  const back = () => setStepIdx((i) => Math.max(i - 1, 0));

  const finish = () => {
    // 把选择落到各处的"当前值"上
    if (state.dialectId) setSelectedDialectId(state.dialectId);
    if (state.tutorId) setSelectedTutorId(state.tutorId);
    completeOnboarding({ startedAt: state.startedAt || new Date().toISOString() });
    navigate('/', { replace: true });
  };

  // 第一步进入时记个开始时间
  useEffect(() => {
    if (!state.startedAt) patch({ startedAt: new Date().toISOString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canNext = (() => {
    if (step === 'dialect') return !!state.dialectId;
    return true; // 其余步骤都允许跳过
  })();

  return (
    <div className="flex min-h-screen flex-col bg-[#fdfaf1] dark:bg-zinc-900">
      {/* 进度 */}
      <header className="flex items-center gap-3 px-5 pt-10 pb-4">
        {stepIdx > 0 ? (
          <button type="button" onClick={back} className="text-zinc-400 active:opacity-60" aria-label="上一步">
            <span className="material-icons">arrow_back</span>
          </button>
        ) : (
          <span className="w-6" />
        )}
        <div className="flex flex-1 gap-1">
          {STEPS.map((s, i) => (
            <span
              key={s}
              className={`h-1 flex-1 rounded-full ${
                i <= stepIdx ? 'bg-primary' : 'bg-zinc-200 dark:bg-zinc-700'
              }`}
            />
          ))}
        </div>
        <span className="w-6" />
      </header>

      <main className="flex-1 px-5 pb-32">
        {loading && step === 'dialect' ? (
          <div className="flex justify-center py-16">
            <span className="material-icons animate-spin text-3xl text-zinc-300">refresh</span>
          </div>
        ) : (
          <>
            <h1 className="text-xl font-bold text-zinc-800 dark:text-zinc-100">{STEP_TITLES[step]}</h1>
            <div className="mt-5">
              {step === 'welcome' && <Welcome />}
              {step === 'language' && <LanguageStep state={state} patch={patch} />}
              {step === 'dialect' && <DialectStep state={state} patch={patch} dialects={dialects} />}
              {step === 'goals' && <GoalsStep state={state} patch={patch} />}
              {step === 'level' && <LevelStep state={state} patch={patch} />}
              {step === 'tutor' && <TutorStep state={state} patch={patch} tutors={tutors} />}
              {step === 'permission' && <PermissionStep state={state} patch={patch} />}
              {step === 'plan' && <PlanStep state={state} patch={patch} />}
            </div>
          </>
        )}
      </main>

      {/* 底部按钮 */}
      <div
        className="fixed bottom-0 left-0 right-0 mx-auto max-w-md border-t border-zinc-100 bg-white/95 px-5 pt-4 backdrop-blur dark:border-zinc-800 dark:bg-zinc-900/95"
        style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
      >
        <button
          type="button"
          onClick={next}
          disabled={!canNext}
          className="w-full rounded-2xl bg-primary py-3.5 text-base font-bold text-white shadow-md transition active:scale-[0.98] disabled:opacity-40"
        >
          {isLast ? '开始学习' : step === 'welcome' ? '开始体验' : '下一步'}
        </button>
        {step !== 'welcome' && !isLast && (
          <button
            type="button"
            onClick={next}
            className="mt-2 w-full py-2 text-xs font-bold text-zinc-400 active:opacity-60"
          >
            跳过这一步
          </button>
        )}
      </div>
    </div>
  );
}

/* ============================================================
 * 各步骤
 * ============================================================ */

function Welcome() {
  return (
    <div>
      <p className="text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
        方言通是一套**引导式**方言课堂。不是背单词，是一课一个真实场景，
        跟着老师听、跟着读、做完练习就能用。
      </p>
      <div className="mt-6 space-y-3">
        {[
          { icon: 'record_voice_over', t: '跟着老师一句一句来', d: '听示范 → 跟读 → 填空 → 换词 → 进场景用一次' },
          { icon: 'fact_check', t: '说没说出来，当场告诉你', d: '识别你说的内容并逐字标出对错' },
          { icon: 'auto_awesome', t: '没有你想学的场景？自己生成', d: '说一句需求就生成一整节课' },
        ].map((f) => (
          <div key={f.t} className="flex gap-3 rounded-2xl bg-white p-4 dark:bg-zinc-800">
            <span className="material-icons text-primary">{f.icon}</span>
            <div>
              <p className="text-sm font-bold text-zinc-800 dark:text-zinc-100">{f.t}</p>
              <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">{f.d}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LanguageStep({ state, patch }) {
  const LANGS = [
    { id: 'zh-CN', label: '普通话（简体）' },
    { id: 'zh-TW', label: '國語（繁體）' },
    { id: 'zh-HK', label: '廣東話' },
  ];
  return (
    <div className="space-y-2">
      <p className="mb-3 text-sm text-zinc-500 dark:text-zinc-400">用来给你做解释和翻译对照。</p>
      {LANGS.map((l) => (
        <Pick
          key={l.id}
          active={state.nativeLanguage === l.id}
          onClick={() => patch({ nativeLanguage: l.id })}
          title={l.label}
        />
      ))}
    </div>
  );
}

function DialectStep({ state, patch, dialects }) {
  const [q, setQ] = useState('');
  const shown = dialects.filter(
    (d) => !q || d.name.includes(q) || d.displayName.includes(q) || (d.region || '').includes(q)
  );
  return (
    <div>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="搜索方言或地区"
        className="mb-3 w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm outline-none focus:border-primary dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
      />
      <p className="mb-3 text-[11px] leading-relaxed text-zinc-400">
        只列出**已开放试用**的方言。每项都标注了实测能力 —— 能听、能说、有没有课程是分开评估的。
      </p>
      <div className="space-y-2">
        {shown.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => patch({ dialectId: d.id })}
            className={`w-full rounded-2xl border p-4 text-left transition-transform active:scale-[0.99] ${
              state.dialectId === d.id
                ? 'border-primary bg-primary/5 dark:bg-primary/10'
                : 'border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-800'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-zinc-800 dark:text-zinc-100">
                {d.displayName}
              </span>
              {state.dialectId === d.id && (
                <span className="material-icons text-primary">check_circle</span>
              )}
            </div>
            <p className="mt-0.5 text-[11px] text-zinc-400">{d.region}</p>
            <div className="mt-2 flex flex-wrap gap-1">
              {(d.badges || []).map((b) => {
                const st = STATUS_STYLE[b.id] || STATUS_STYLE.unavailable;
                return (
                  <span key={b.key} className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${st.cls}`}>
                    {b.label}：{st.label}
                  </span>
                );
              })}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function GoalsStep({ state, patch }) {
  const toggle = (id) => {
    const cur = state.goals || [];
    patch({ goals: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };
  return (
    <div className="space-y-2">
      <p className="mb-3 text-sm text-zinc-500 dark:text-zinc-400">可以多选，也可以都不选。</p>
      {GOALS.map((g) => (
        <button
          key={g.id}
          type="button"
          onClick={() => toggle(g.id)}
          className={`flex w-full items-center gap-3 rounded-2xl border p-4 text-left ${
            (state.goals || []).includes(g.id)
              ? 'border-primary bg-primary/5 dark:bg-primary/10'
              : 'border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-800'
          }`}
        >
          <span className="material-icons text-primary">{g.icon}</span>
          <span className="flex-1">
            <span className="block text-sm font-bold text-zinc-800 dark:text-zinc-100">{g.label}</span>
            <span className="block text-xs text-zinc-400">{g.desc}</span>
          </span>
          {(state.goals || []).includes(g.id) && (
            <span className="material-icons text-primary">check_circle</span>
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * A08 水平评估 —— 听辨测试
 *
 * 需求文档要求「听辨、词汇、发音或跳过」。这里做听辨，理由见 lib/levelTest.js。
 *
 * 交互：播一句方言 → 选普通话意思 → 出分 → 映射到起始课程等级。
 * 全程可以跳过 —— 引导不该要求新用户先证明自己。
 */
function LevelStep({ state, patch }) {
  const [phase, setPhase] = useState('intro'); // intro | test | result
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  // 逐题作答记录：弱项分析需要知道**哪个等级**的题答错了，
  // 只统计总答对数是不够的（总分高但高级全错，说明基础扎实、地道表达薄弱）。
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [audioErr, setAudioErr] = useState(null);
  const [loading, setLoading] = useState(false);

  /** 取当前方言的课程，从中出题 */
  const start = async () => {
    setLoading(true);
    setAudioErr(null);
    try {
      const d = state.dialectId || getSelectedDialectId();
      const res = await api.get('/api/curriculum', { params: { dialect: d } });
      const data = res.data?.data || res.data;
      const lessons = [
        ...(data.groups || []).flatMap((g) => g.lessons),
        ...(data.generatedGroups || []).flatMap((g) => g.lessons),
      ];

      // ⚠️ 这里曾写死 `lessons.slice(0, 6)`。课程列表按
      // 初级(5)→中级(5)→高级(5) 排列，所以切片实际只覆盖
      // 「5 节初级 + 1 节中级」—— 高级句子一句都测不到，
      // 高级档因此永远不可达。
      // 现在改用按等级均衡抽样，保证三个等级都有题。
      const sample = selectLessonSample(lessons, 5);

      // 列表接口只带 samplePhrase，需要逐课取完整 phrases
      const detailed = await Promise.all(
        sample.map((l) =>
          api
            .get(`/api/curriculum/${l.id}`)
            .then((r) => r.data?.data || r.data)
            .catch(() => null)
        )
      );
      const qs = buildQuestions(detailed.filter(Boolean), TEST_SIZE);
      if (!qs.length) {
        setAudioErr('这个方言的课程还不够出题，先跳过这一步吧');
        return;
      }
      setQuestions(qs);
      setIdx(0);
      setCorrectCount(0);
      setAnswers([]);
      setResult(null);
      setPicked(null);
      setPhase('test');
      /*
       * 预热前两题：第一题马上要播，第二题大概率紧接着要播。
       * 只热两条 —— 十条全并发会同时打满 TTS 配额，反而更容易超时。
       */
      fetchAudioBlob(qs[0]);
      if (qs[1]) fetchAudioBlob(qs[1]);
      // 稍等一下再播，避免和页面切换的渲染撞在一起
      setTimeout(() => play(qs[0]), 350);
    } catch (e) {
      setAudioErr(e?.message || '出题失败');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 测评语音的预取缓存。
   *
   * 为什么需要：测评每答一题都要一段方言语音，而这段语音是**实时合成**的
   * （POST /api/tts）。服务在海外，单次往返实测 2~5 秒，于是旧逻辑表现为
   * 「答完一题要干等两三秒才开口」，节奏一乱就像"隔一题才响一次"。
   *
   * 题目是随机抽的，没法预生成固定文件；但可以**提前把下一题合成好**，
   * 把这两三秒藏进"显示答题对错"的那段间隙里。用户看到反馈时音频已在
   * 本地，点下一题几乎立刻出声。
   *
   * 结构：text -> Promise<Blob|null>。存 Promise 是为了让并发调用共享同一个
   * 请求（同一句被预取 + 播放同时触发时不会重复请求、重复计费）。
   */
  const audioCache = useRef(new Map());

  /** 取一句的音频 Blob（命中缓存则直接复用进行中的请求） */
  const fetchAudioBlob = useCallback((question) => {
    if (!question?.cantonese) return Promise.resolve(null);
    const key = question.cantonese;
    const cache = audioCache.current;
    if (cache.has(key)) return cache.get(key);

    const d = state.dialectId || getSelectedDialectId();
    const p = api
      .post(
        '/api/tts',
        { text: key, language: 'cantonese', dialect: d },
        { responseType: 'blob' }
      )
      .then((r) => r.data)
      .catch(() => null);
    cache.set(key, p);
    return p;
  }, [state.dialectId]);
  const play = async (q) => {
    const question = q || questions[idx];
    if (!question) return;
    setPlaying(true);
    setAudioErr(null);
    try {
      /*
       * 走缓存：若这一题在答题间隙已被预取，这里拿到的就是已完成的 Blob，
       * 只剩 createObjectURL + play，几乎无等待。
       * Blob 每次新建 URL 并在结束时 revoke —— 不能缓存 URL 本身，
       * 因为 revoke 后地址失效，下一轮就播不出来了。
       */
      const blob = await fetchAudioBlob(question);
      if (!blob) throw new Error('语音加载失败');
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => {
        setPlaying(false);
        URL.revokeObjectURL(url);
      };
      audio.onerror = () => {
        setPlaying(false);
        URL.revokeObjectURL(url);
      };
      await audio.play().catch(() => setPlaying(false));
    } catch (e) {
      setPlaying(false);
      setAudioErr('播放失败：' + (e?.message || '未知错误'));
    }
  };

  /** 选择答案 */
  const choose = (i) => {
    if (picked !== null) return;
    setPicked(i);
    const q = questions[idx];
    const ok = i === q.answerIndex;
    const nextCount = correctCount + (ok ? 1 : 0);
    const nextAnswers = [...answers, { level: q.level, correct: ok }];
    setCorrectCount(nextCount);
    setAnswers(nextAnswers);

    setTimeout(() => {
      if (idx + 1 < questions.length) {
        setIdx(idx + 1);
        setPicked(null);
        /*
         * 先预热下下题，再播下一题 —— 顺序有意为之：
         * 下一题的音效应该在这段（用户看对错的 1~2 秒）里已经合成完，
         * play 时基本直接出声；同时顺手把再下一题也放进队列，
         * 让流水线一直领先一步。
         */
        if (questions[idx + 2]) fetchAudioBlob(questions[idx + 2]);
        setTimeout(() => play(questions[idx + 1]), 250);
      } else {
        // 用完整作答记录做定档：除了总分，还要看各等级表现，
        // 这样"高级题全错"的人不会被推到高级课去。
        const r = analyzeResult(nextAnswers);
        setResult(r);
        patch({
          level: r.level.levelId,
          levelTest: { correct: r.correct, total: r.total },
        });
        setPhase('result');
      }
    }, 900);
  };

  /* ---------- 说明页 ---------- */
  if (phase === 'intro') {
    return (
      <div>
        <p className="text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
          我放 {TEST_SIZE} 句方言，你选出它的普通话意思。**不用开口**，随便猜也行。
        </p>
        <div className="mt-5 rounded-2xl bg-white p-4 dark:bg-zinc-800">
          <p className="text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
            做这个是为了让你**从合适的难度**开始，而不是为了考试。
            答得不好就从第一课开始，没任何影响。不想做可以直接跳过。
          </p>
          <p className="mt-2 text-xs leading-relaxed text-zinc-400">
            题里会掺几句高级课的内容 —— 答不上来很正常，那是为了让「听懂多少」分得更准。
          </p>
        </div>
        {audioErr && (
          <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
            {audioErr}
          </p>
        )}
        <button
          type="button"
          onClick={start}
          disabled={loading}
          className="mt-5 w-full rounded-2xl bg-primary py-3 text-sm font-bold text-white disabled:opacity-40"
        >
          {loading ? '出题中…' : `开始测试（${TEST_SIZE} 题）`}
        </button>
      </div>
    );
  }

  /* ---------- 结果页 ---------- */
  if (phase === 'result') {
    const r = result;
    const lv = r?.level;
    return (
      <div>
        <div className="rounded-2xl bg-white p-5 text-center dark:bg-zinc-800">
          <p className="text-3xl font-bold text-primary">
            {r ? r.correct : correctCount}
            <span className="text-lg text-zinc-400">/{r ? r.total : questions.length}</span>
          </p>
          <p className="mt-2 text-sm font-bold text-zinc-800 dark:text-zinc-100">{lv?.label}</p>
          <p className="mt-1 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">{lv?.desc}</p>
        </div>

        {/* 逐级表现：让用户看到自己"哪一段行、哪一段不行" */}
        {r && Object.keys(r.byLevel).length > 1 && (
          <div className="mt-3 rounded-2xl bg-white p-4 dark:bg-zinc-800">
            <p className="mb-2 text-xs font-bold text-zinc-500">各段表现</p>
            <div className="space-y-2">
              {Object.entries(r.byLevel).map(([key, s]) => {
                const names = { beginner: '初级', intermediate: '中级', advanced: '高级' };
                return (
                  <div key={key} className="flex items-center gap-2">
                    <span className="w-8 shrink-0 text-xs text-zinc-500">{names[key] || key}</span>
                    <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-700">
                      <span
                        className="block h-full rounded-full bg-primary"
                        style={{ width: `${Math.round(s.ratio * 100)}%` }}
                      />
                    </span>
                    <span className="w-10 shrink-0 text-right text-xs text-zinc-400">
                      {s.correct}/{s.total}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 弱项提示 */}
        {r?.weakSpots?.map((w) => (
          <div
            key={w.level}
            className="mt-3 rounded-2xl bg-amber-50 p-4 dark:bg-amber-900/20"
          >
            <p className="text-xs font-bold text-amber-700 dark:text-amber-300">{w.label}</p>
            <p className="mt-1 text-xs leading-relaxed text-amber-700/80 dark:text-amber-300/80">
              {w.advice}
            </p>
          </div>
        ))}

        {/* 学习路径 */}
        {r?.pathAdvice?.length > 0 && (
          <div className="mt-3 rounded-2xl bg-white p-4 dark:bg-zinc-800">
            <p className="mb-2 text-xs font-bold text-zinc-500">建议路线</p>
            <div className="space-y-2.5">
              {r.pathAdvice.map((p, i) => (
                <div key={p.level} className="flex gap-3">
                  <span
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                      p.isStart
                        ? 'bg-primary text-white'
                        : 'bg-zinc-100 text-zinc-400 dark:bg-zinc-700'
                    }`}
                  >
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-bold text-zinc-700 dark:text-zinc-200">
                      {p.levelName}
                      <span className="ml-2 font-normal text-zinc-400">{p.stage}</span>
                    </span>
                    <span className="block text-xs leading-relaxed text-zinc-400">{p.desc}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => {
            setPhase('intro');
            setPicked(null);
            setCorrectCount(0);
            setAnswers([]);
            setResult(null);
          }}
          className="mt-4 w-full rounded-2xl border border-zinc-200 py-2.5 text-xs font-bold text-zinc-500 dark:border-zinc-700"
        >
          重做一次
        </button>
      </div>
    );
  }

  /* ---------- 答题页 ---------- */
  const q = questions[idx];
  return (
    <div>
      <div className="mb-3 flex items-center justify-between text-xs text-zinc-400">
        <span>
          第 {idx + 1} / {questions.length} 题
        </span>
        <span>已答对 {correctCount}</span>
      </div>

      <button
        type="button"
        onClick={() => play()}
        disabled={playing}
        className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-4 text-sm font-bold text-white disabled:opacity-60"
      >
        <span className="material-icons">{playing ? 'volume_up' : 'play_circle'}</span>
        {playing ? '正在播放…' : '再听一次'}
      </button>

      {audioErr && (
        <p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
          {audioErr}
        </p>
      )}

      <p className="mt-4 mb-2 text-xs font-bold text-zinc-500">这句话是什么意思？</p>
      <div className="space-y-2">
        {q.options.map((o, i) => {
          const isAnswer = i === q.answerIndex;
          const chosen = picked === i;
          let cls = 'border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-800';
          if (picked !== null) {
            if (isAnswer) cls = 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30';
            else if (chosen) cls = 'border-rose-400 bg-rose-50 dark:bg-rose-900/30';
          }
          return (
            <button
              key={o}
              type="button"
              onClick={() => choose(i)}
              disabled={picked !== null}
              className={`flex w-full items-center gap-2 rounded-2xl border p-3.5 text-left text-sm text-zinc-700 dark:text-zinc-200 ${cls}`}
            >
              <span className="flex-1">{o}</span>
              {picked !== null && isAnswer && (
                <span className="material-icons !text-base text-emerald-500">check_circle</span>
              )}
              {picked !== null && chosen && !isAnswer && (
                <span className="material-icons !text-base text-rose-500">cancel</span>
              )}
            </button>
          );
        })}
      </div>

      {picked !== null && (
        <p className="mt-3 text-center text-xs text-zinc-400">
          原句：{q.cantonese}
          {q.jyutping ? `　${q.jyutping}` : ''}
        </p>
      )}
    </div>
  );
}

function TutorStep({ state, patch, tutors }) {
  return (
    <div className="space-y-2">
      <p className="mb-3 text-sm text-zinc-500 dark:text-zinc-400">
        老师会陪你走完整节课：开场讲解、示范读音、点评你的发音。
      </p>
      {tutors.length === 0 && (
        <p className="rounded-2xl bg-white p-4 text-xs text-zinc-400 dark:bg-zinc-800">
          暂时没取到老师列表，可以跳过，之后在「发现」里选。
        </p>
      )}
      {tutors.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => patch({ tutorId: t.id })}
          className={`flex w-full items-center gap-3 rounded-2xl border p-4 text-left ${
            state.tutorId === t.id
              ? 'border-primary bg-primary/5 dark:bg-primary/10'
              : 'border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-800'
          }`}
        >
          {t.portrait ? (
            <img src={t.portrait} alt="" className="h-12 w-12 rounded-full object-cover" />
          ) : (
            <span className="material-icons text-3xl text-primary">face</span>
          )}
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-bold text-zinc-800 dark:text-zinc-100">{t.name}</span>
            <span className="block truncate text-xs text-zinc-400">{t.tagline}</span>
          </span>
          {state.tutorId === t.id && <span className="material-icons text-primary">check_circle</span>}
        </button>
      ))}
    </div>
  );
}

/**
 * 权限说明（文档 A10）
 *
 * 关键：**解释为什么**，而不是只弹系统授权框。
 * 用户不知道麦克风用来干什么时，拒绝率很高；
 * 而且拒了之后再想打开要去系统设置，成本更高。
 */
function PermissionStep({ state, patch }) {
  const items = [
    {
      icon: 'mic',
      title: '麦克风',
      why: '跟读练习要用它听你说了什么。**录音只用于当次识别，识别完即丢弃**，不会保存、不会上传留档。',
    },
    {
      icon: 'volume_up',
      title: '音频播放',
      why: '老师的示范读音和点评需要播放声音。声音由云端合成后临时下发。',
    },
    {
      icon: 'notifications',
      title: '通知',
      why: '可选。用来提醒你复习 —— 不打开也不影响学习。',
    },
  ];
  return (
    <div>
      <p className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">
        下面几项权限会在用到时由浏览器询问。先说明它们各自用来做什么：
      </p>
      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.title} className="flex gap-3 rounded-2xl bg-white p-4 dark:bg-zinc-800">
            <span className="material-icons text-primary">{it.icon}</span>
            <div>
              <p className="text-sm font-bold text-zinc-800 dark:text-zinc-100">{it.title}</p>
              <p className="mt-0.5 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">{it.why}</p>
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => patch({ micExplained: true })}
        className={`mt-4 w-full rounded-2xl border p-3 text-sm font-bold ${
          state.micExplained
            ? 'border-primary bg-primary/5 text-primary'
            : 'border-zinc-200 text-zinc-500 dark:border-zinc-700'
        }`}
      >
        {state.micExplained ? '✓ 我知道了' : '我知道了'}
      </button>
    </div>
  );
}

function PlanStep({ state, patch }) {
  return (
    <div>
      <p className="mb-3 text-sm text-zinc-500 dark:text-zinc-400">
        课程都很短（一节课 10 步左右）。选一个你**真能做到**的时长，比选一个大的更有用。
      </p>
      <div className="space-y-2">
        {DAILY_PLANS.map((p) => (
          <Pick
            key={p.id}
            active={state.planId === p.id}
            onClick={() => patch({ planId: p.id })}
            title={`${p.label}　${p.desc}`}
            desc={p.id === 'normal' ? '推荐' : ''}
          />
        ))}
      </div>
      <p className="mt-4 text-[11px] leading-relaxed text-zinc-400">
        这个时长只作为目标提示，不做强制限制 —— 我们不会因为"今天没学够"就锁住功能。
      </p>
    </div>
  );
}

function Pick({ active, onClick, title, desc }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-2xl border p-4 text-left transition-transform active:scale-[0.99] ${
        active
          ? 'border-primary bg-primary/5 dark:bg-primary/10'
          : 'border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-800'
      }`}
    >
      <span className="flex-1">
        <span className="block text-sm font-bold text-zinc-800 dark:text-zinc-100">{title}</span>
        {desc && <span className="block text-xs text-zinc-400">{desc}</span>}
      </span>
      {active && <span className="material-icons text-primary">check_circle</span>}
    </button>
  );
}
